import React, { useCallback, useEffect, useRef, useState } from 'react'
import { isEmpty, path } from 'ramda'
import { FastField, Formik } from 'formik'
import * as yup from 'yup'
import {
  Modal,
  CurrencyField,
  FormField,
  Checkbox,
  RadioGroup,
  Radio,
  TextField,
  CurrencyText,
  FormLoader,
  TextInput,
  Label,
} from 'components'
import { currencyUtils } from '../../../../utils'
import { walletApiConstants, walletApiEvents } from '../../../api/wallet'
import styles from './DepositTransferModal.module.scss'
import { useDispatch } from 'react-redux'
import { debounce } from 'lodash-es'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { $TSFixMe } from 'types/ts-migrate'

const { transferReasons } = walletApiConstants

const validationSchema = yup.object().shape({
  amount: yup
    .number()
    .required('required')
    .test('amount', function (amount: $TSFixMe) {
      if (amount <= 0) {
        return this.createError({
          path: this.path,
          message: 'must be greater than 0',
        })
      }
      if (amount > this.parent.depositAccountBalance) {
        return this.createError({
          path: this.path,
          message: `can't be greater than deposit balance`,
        })
      }
      return true
    }),
  transferReason: yup
    .object()
    .shape({
      type: yup.string().required('required'),
      reason: yup.string(),
    })
    .test('transferReason', function (transferReason) {
      if (transferReason.type === transferReasons.other && !transferReason.reason?.trim()) {
        return this.createError({
          path: `${this.path}.reason`,
          message: `required`,
        })
      }
      if (
        transferReason.type === transferReasons.other &&
        transferReason.reason !== undefined &&
        transferReason.reason.length > 30
      ) {
        return this.createError({
          path: `${this.path}.reason`,
          message: `can't be longer than 30 characters`,
        })
      }
      return true
    })
    .required(),
  isConfirmed: yup.bool().oneOf([true], 'Required!'),
})

const ButtonContainer = ({ children }: { children: React.ReactNode }): React.ReactElement => (
  <div className={styles['button-container']}>{children}</div>
)

interface TransferReason {
  type: string
  reason?: string
}

interface TransferFormValues {
  amount: number
  transferReason: TransferReason
  isConfirmed: boolean
  partyId?: string
  account?: string
  asset?: string
  /** `null` while the balance is still loading — see `walletApiSelectors.getDepositAccountBalance`. */
  depositAccountBalance?: number | null
}

interface DepositTransferModalProps {
  isOpen: boolean
  onClose: () => void
  contactName?: string
  partyId?: string
  account?: string
  asset?: string
  /** `null` while the balance is still loading — see `walletApiSelectors.getDepositAccountBalance`. */
  depositAccountBalance?: number | null
  onTransfer: (payload: { body: TransferFormValues; params: { partyId?: string } }) => void
  isLoading?: boolean
  isSubmitting?: boolean
}

const DepositTransferModal = ({
  isOpen,
  onClose,
  contactName,
  partyId,
  account,
  asset,
  depositAccountBalance,
  onTransfer,
  isLoading,
  isSubmitting,
}: DepositTransferModalProps): React.ReactElement => {
  const dispatch = useDispatch()
  const reasonInputRef = useRef<HTMLInputElement>(null)

  const handleClose = useCallback(
    resetForm => {
      resetForm()
      onClose()
    },
    [onClose],
  )

  useEffect(() => {
    // `partyId` is an optional prop and a path segment on BALANCE.
    isOpen && partyId && dispatch(walletApiEvents.balance_request({ partyId }))
  }, [isOpen, dispatch, partyId])

  const [wasSubmitting, setWasSubmitting] = useState(false)

  useEffect(() => {
    if (isSubmitting) {
      setWasSubmitting(true)
    }
  }, [isSubmitting])

  const debouncedClose = useCallback(
    debounce(
      () => {
        onClose()
        setWasSubmitting(false)
      },
      1000,
      { leading: false },
    ),
    [onClose, setWasSubmitting],
  )

  /**
   * @todo error handling
   */
  useEffect(() => {
    if (wasSubmitting) {
      debouncedClose()
    }
  }, [wasSubmitting, onClose, debouncedClose])

  return (
    <Formik
      enableReinitialize
      validateOnMount={false}
      validationSchema={validationSchema}
      initialValues={{
        amount: 0,
        transferReason: { type: '' },
        isConfirmed: false,
        partyId,
        account,
        asset,
        depositAccountBalance,
      }}
      onSubmit={values => onTransfer({ body: values, params: { partyId: values.partyId } })}
    >
      {({
        values,
        errors,
        touched,
        submitCount,
        isValid,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        resetForm,
      }) => {
        const handleOtherReason = ({ target }: any): void => {
          setFieldValue('transferReason', {
            reason: target.value,
            type: 'Other',
          })
        }

        const formLoaderState = isLoading
          ? 'loading'
          : isSubmitting
          ? 'submitting'
          : !isEmpty(errors)
          ? 'error'
          : undefined

        const formattedAmount: number = currencyUtils.formatCurrency(values?.amount || 0)
        const confirmationLabel = (
          <div>
            I confirm I'm transferring <strong>{formattedAmount}</strong> from{' '}
            <strong>{contactName as string}'s</strong> deposit wallet to their main transactional wallet.
          </div>
        )

        return (
          <Modal isOpen={isOpen} onClose={() => handleClose(resetForm)}>
            <Modal.Title>Transfer deposit</Modal.Title>
            <Modal.Body>
              <FormLoader
                onSubmit={handleSubmit}
                state={formLoaderState}
                buttonProps={{
                  children: 'Transfer deposit',
                  disabled: !isValid || ['loading', 'error'].includes(formLoaderState as string),
                }}
                ButtonContainer={ButtonContainer}
              >
                <FormField>
                  <FastField
                    name="amount"
                    render={({ field }: any) => (
                      <CurrencyField
                        {...field}
                        label="Transfer amount"
                        error={(path(['amount'], values) || submitCount > 0) && path(['amount'], errors)}
                        onChange={({ target }: any) => setFieldValue('amount', target.value)}
                      />
                    )}
                  />
                </FormField>
                <div className={styles['balance-info']}>
                  <Label circular className={styles['balance-label']}>
                    Deposit balance: <CurrencyText>{depositAccountBalance ?? 0}</CurrencyText>
                  </Label>
                </div>
                <FormField>
                  <FastField
                    name="transferReason"
                    render={({ field }: any) => (
                      <RadioGroup
                        {...field}
                        name="transferReason.type"
                        stackRadios={true}
                        label="Reason for transfer"
                        labelHint="This will appear on the deposit statement"
                        onChange={e => {
                          setFieldValue('transferReason.type', e.target.value)
                          if (e.target.value === transferReasons.other) {
                            setTimeout(() => reasonInputRef.current?.focus(), 0)
                          }
                        }}
                        error={
                          (path(['transferReason', 'type'], values) || submitCount > 0) &&
                          path(['transferReason', 'type'], errors)
                        }
                      >
                        <Radio
                          allowUncheck={false}
                          name="unsettledInvoice"
                          value={transferReasons.unsettledInvoice}
                          label="Unsettled invoice"
                        />
                        <Radio
                          allowUncheck={false}
                          name="damageClaim"
                          value={transferReasons.damageClaim}
                          label="Damage claim"
                        />
                        <Radio
                          allowUncheck={false}
                          name="leaseEnding"
                          value={transferReasons.leaseEnding}
                          label="Lease ending"
                        />
                        <Radio allowUncheck={false} name="other" value={transferReasons.other} label="Other" />
                      </RadioGroup>
                    )}
                  />
                </FormField>
                <FormField
                  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; style: { display: strin... Remove this comment to see the full error message
                  style={{
                    display:
                      // @ts-expect-error ts-migrate(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message
                      values.transferReason === transferReasons.other ||
                      path(['type'], values.transferReason) === transferReasons.other
                        ? 'block'
                        : 'none',
                    paddingTop: '0',
                  }}
                >
                  <FastField
                    name="transferReason.reason"
                    render={({ field }: any) => (
                      <TextField
                        {...field}
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name={field.name}
                            onChange={handleOtherReason}
                            onBlur={field.onBlur}
                            ref={reasonInputRef}
                          />
                        }
                        label="Other reason"
                        error={
                          (path(['transferReason', 'reason'], touched) || submitCount > 0) &&
                          path(['transferReason', 'reason'], errors)
                        }
                      />
                    )}
                  />
                </FormField>
                <FormField>
                  <Checkbox
                    className={styles.confirmation}
                    name="isConfirmed"
                    label={confirmationLabel}
                    checked={values.isConfirmed}
                    onChange={(e: any) => setFieldValue('isConfirmed', e.checked)}
                    onBlur={handleBlur}
                    error={submitCount > 0 && path(['isConfirmed'], errors)}
                  />
                </FormField>
              </FormLoader>
            </Modal.Body>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default DepositTransferModal
